import { ApiBase } from '@bymason/api-base';
import { AccountModel, InviteUserModel, MFAEnforcementModel, UserModel } from './types';

export * from './types';

class Account extends ApiBase {
  private static instance: Account;

  private constructor() {
    super();
  }

  /**
   * There is no need for consumers to ever call this method as its purely used to enforce a singleton module pattern.
   * @returns An Account instance
   */
  public static getInstance(): Account {
    if (!Account.instance) {
      Account.instance = new Account();
      Account.instance.configure({
        fleet: 'global',
      });
    }

    return Account.instance;
  }

  /**
   * Gets an account.
   * @param clientToken token required for proper authorization.
   * @returns either an account model or void
   */
  public async getAccount(clientToken: string) {
    const url = this.getEndpointTemplate('account/info');

    const dataResponse = await this.callJsonEndpoint<AccountModel>({
      clientToken,
      method: 'GET',
      url,
    });

    return dataResponse;
  }

  /**
   * Create a user and invite them to join an account via email
   * @param options metadata required to invite an user.
   * @param clientToken token required for proper authorization.
   * @returns either an account model or void
   */
  public async inviteUser(options: InviteUserModel, clientToken: string) {
    const url = this.getEndpointTemplate('account/invite');

    const dataResponse = await this.callVoidEndpoint({
      body: JSON.stringify(options),
      clientToken,
      method: 'POST',
      url,
    });

    return dataResponse;
  }

  /**
   * Delete user by id
   * @param id metadata required to invite an user.
   * @param clientToken token required for proper authorization.
   * @returns either an account model or void
   */
  public async deletUser(id: string, clientToken: string) {
    const url = this.getEndpointTemplate(`user/${id}`);
    const dataResponse = await this.callVoidEndpoint({
      clientToken,
      method: 'DELETE',
      url,
    });

    return dataResponse;
  }

  /**
   * @param clientToken token required for proper authorization.
   * @param enforce boolean value to enforce MFA.
   * @returns either an account model or void
   */

  public async enforceMFA(enforce: boolean, clientToken: string) {
    const url = this.getEndpointTemplate('account/mfa');

    const dataResponse = await this.callJsonEndpoint<MFAEnforcementModel>({
      body: JSON.stringify({ enforce }),
      clientToken,
      method: 'POST',
      url,
    });

    return dataResponse;
  }

  /**
   *
   * @param clientToken token required for proper authorization.
   * @returns MFAEnforcementModel object
   */
  public async getMFAStatus(clientToken: string) {
    const url = this.getEndpointTemplate('account/mfa');

    const dataResponse = await this.callJsonEndpoint<MFAEnforcementModel>({
      clientToken,
      method: 'GET',
      url,
    });

    return dataResponse;
  }

  /**
   * @param userId user id to reset MFA for.
   * @param clientToken token required for proper authorization.
   * @returns void
   */
  public async resetUserMFA(userId: string, clientToken: string) {
    const url = this.getEndpointTemplate(`user/${userId}/mfa`);

    const dataResponse = await this.callVoidEndpoint({
      clientToken,
      method: 'DELETE',
      url,
    });

    return dataResponse;
  }

  /**
   * Gets an account.
   * @param clientToken token required for proper authorization.
   * @returns either an account model or void
   */
  public async getUser(userId: string, clientToken: string) {
    const url = this.getEndpointTemplate(`user/${userId}`);

    const dataResponse = await this.callJsonEndpoint<UserModel>({
      clientToken,
      method: 'GET',
      url,
    });

    return dataResponse;
  }
}

export const accountClient = Account.getInstance();
